import { AssetStatus as AssetStatusEnum } from 'packages/types/assets';

export type AssetStatus = AssetStatusEnum.UPLOAD_IN_PROGRESS | AssetStatusEnum.COMPLETED | AssetStatusEnum.CANCELLED;

export type UploaderStages = 'validating' | 'gettingUploadUrl' | 'uploading' | 'uploaded' | 'statusUpdate';
export type UploaderErrorType = 'generic' | 'mimeType' | 'size' | 'resolution' | 'cancelled';

export type UploaderError = {
    status: 'error';
    stage: UploaderStages;
    type: UploaderErrorType;
};
type UploaderBaseState = {
    uploadingCount?: number;
    totalCount?: number;
    uploadsProgress?: Map<string, 'UPLOAD_IN_PROGRESS' | 'SUCCESS' | 'ERROR'>;
};

type UploaderProgressState<TStage extends UploaderStages, TFields extends object = object> = {
    stage: TStage;
    status: 'inProgress';
    hideNotification?: boolean;
} & TFields;

export type UploaderProgress =
    | UploaderProgressState<'validating'>
    | UploaderProgressState<'gettingUploadUrl'>
    | UploaderProgressState<'statusUpdate', { value: AssetStatus; uploadData?: any }>
    | UploaderProgressState<'uploading', { total: number; loaded: number; percent: number }>
    | UploaderProgressState<'uploaded', { url: string }>;

export type Task<T> = UploaderError | { status: 'success'; data: T };

export type UploaderState =
    | ({ status: 'ready' } & UploaderBaseState)
    | (UploaderProgress & UploaderBaseState)
    | ({ status: 'success'; hideNotification?: boolean } & UploaderBaseState)
    | (UploaderError & UploaderBaseState);

export const ACCEPT = {
    image: [
        '.jpg',
        '.jpeg',
        'image/jpeg',
        '.heic',
        'image/heic',
        '.heif',
        'image/heif',
        '.avif',
        'image/avif',
        '.bmp',
        'image/bmp',
        '.png',
        'image/png',
        '.webp',
        'image/webp',
        '.tif',
        '.tiff',
        'image/tiff',
        '.svg',
        'image/svg+xml',
        '.eps',
        'application/eps',
        'application/x-eps',
        'image/eps',
        'image/x-eps',
        'application/postscript',
        '.rw2',
        'image/x-panasonic-rw2',
        '.dng',
        'image/x-adobe-dng',
        '.orf',
        'image/x-olympus-orf',
        '.cr3',
        'image/x-canon-cr3',
        '.nef',
        'image/x-nikon-nef',
    ],
    video: [
        '.avi',
        'video/avi',
        '.mp4',
        'video/mp4',
        '.mov',
        'video/quicktime',
        '.mpeg',
        '.mpg',
        'video/mpeg',
        '.webm',
        'video/webm',
    ],
    audio: [
        '.aac',
        'audio/aac',
        '.aif',
        '.aiff',
        'audio/aif',
        'audio/aiff',
        'audio/x-aiff',
        '.mp3',
        'audio/mp3',
        'audio/mpeg',
        '.wav',
        'audio/wav',
        'audio/x-wav',
    ],
    document: [
        '.sketch',
        'application/sketch',
        '.psd',
        'image/vnd.adobe.photoshop',
        'image/psd',
        'application/psd',
        'application/photoshop',
        'application/x-photoshop',
    ],
    // Temporarily outside of document category due to validation requirements, will be moved back once fully supported
    idml: [
        '.idml',
        'application/vnd.adobe.indesign-idml-package',
        '.zip',
        'application/zip',
        'application/x-zip-compressed',
    ],

    get all() {
        return [...this.document, ...this.image, ...this.video, ...this.audio];
    },
};

export type AssetType = keyof typeof ACCEPT;

export type UploadService = (props: {
    file: File;
    customerId: number;
    collectionId?: number;
    abortSignal: AbortSignal;
    assetTypes: AssetType[];
    hideNotification?: boolean;
}) => AsyncGenerator<UploaderState, void, void>;
